import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

interface InvoiceItemRow {
  id: number;
  service_type: 'visa_pack' | 'trip_booking' | 'rental_car';
  description: string;
  quantity: number | string;
  unit_price: number | string;
  amount: number | string;
}

interface Invoice {
  id: number;
  reference: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  service_type: 'visa_pack' | 'trip_booking' | 'rental_car';
  service_id: number;
  items?: InvoiceItemRow[];
  amount: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  currency: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  issue_date: string;
  due_date: string;
  paid_date?: string;
  notes?: string;
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  gold: "#d3be76",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: `1px solid ${colors.border}`,
  borderRadius: "8px",
  fontSize: "13px",
  outline: "none",
  color: colors.text,
  backgroundColor: colors.bgSubtle,
  boxSizing: "border-box",
};

const actionBtnStyle: React.CSSProperties = {
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: `1px solid ${colors.borderLight}`,
  backgroundColor: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const getStatusClass = (status: string) => {
  switch (status) {
    case "paid": return { bg: "#dcfce7", text: "#166534", border: "#bbf7d0" };
    case "pending": return { bg: "#fef3c7", text: "#92400e", border: "#fde68a" };
    case "overdue": return { bg: "#fee2e2", text: "#991b1b", border: "#fecaca" };
    case "cancelled": return { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" };
    default: return { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" };
  }
};

const getServiceTypeLabel = (serviceType: string) => {
  switch (serviceType) {
    case "visa_pack": return "Visa Pack";
    case "trip_booking": return "Trip Booking";
    case "rental_car": return "Rental Car";
    default: return serviceType;
  }
};

const getItemsSummary = (invoice: Invoice) => {
  if (invoice.items && invoice.items.length > 0) {
    const first = invoice.items[0];
    return {
      primary: first.description || getServiceTypeLabel(first.service_type),
      extra: invoice.items.length - 1,
      count: invoice.items.length,
    };
  }
  return { primary: getServiceTypeLabel(invoice.service_type), extra: 0, count: invoice.service_type ? 1 : 0 };
};

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{
              width: 200,
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: 60,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 280,
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <div style={{
            width: 80,
            height: 38,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 8,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div style={{
            width: 120,
            height: 38,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 8,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
      </div>

      {/* Main Card Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        marginTop: "15px",
      }}>
        {/* Filter Bar Skeleton */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
          <div className="row g-3 align-items-end">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="col">
                <div style={{
                  width: i === 0 ? "100%" : i < 3 ? "100%" : i === 3 ? "100%" : i === 4 ? "80%" : "80%",
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Header Skeleton */}
        <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div className="row">
            {["Reference", "Customer", "Items", "Amount", "Status", "Actions"].map((_, i) => (
              <div key={i} className="col" style={{ padding: "0 8px" }}>
                <div style={{
                  width: i === 0 ? 70 : i === 1 ? 80 : i === 2 ? 80 : i === 3 ? 60 : i === 4 ? 60 : 50,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Rows Skeleton */}
        {[...Array(8)].map((_, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              padding: "14px 16px",
              borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
              display: "flex",
              alignItems: "center"
            }}
          >
            {[...Array(6)].map((_, colIndex) => (
              <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                <div style={{
                  width: colIndex === 0 ? 100 : colIndex === 1 ? 140 : colIndex === 2 ? 160 : colIndex === 3 ? 80 : colIndex === 4 ? 80 : 70,
                  height: colIndex === 1 ? 16 : 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                {colIndex === 1 && (
                  <div style={{
                    width: 100,
                    height: 12,
                    marginTop: 4,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 3,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                )}
                {colIndex === 2 && (
                  <div style={{
                    width: 60,
                    height: 18,
                    marginTop: 4,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 12,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Pagination Skeleton */}
        <div style={{
          padding: "12px 20px",
          borderTop: "1px solid #edf2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{
            width: 150,
            height: 16,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div style={{ display: "flex", gap: "6px" }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{
                width: i === 0 || i === 4 ? 70 : 32,
                height: 32,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ListInvoices = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  // Récupération du token
  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const formatAmount = (amount: number | string): string => {
    if (amount === null || amount === undefined) return "0.00";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return isNaN(numAmount) ? "0.00" : numAmount.toFixed(2);
  };

  const handleDownloadPDF = async (invoiceId: number) => {
  try {
    setDownloadingPdf(invoiceId);
    const response = await fetch(`${API_URL}/invoices/${invoiceId}/download-pdf`, {
      method: "GET",
      headers: {
        Accept: "application/pdf",
        ...config_token.headers,
      },
    });

    if (!response.ok) throw new Error("Failed to download PDF");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    link.download = `invoice-${invoice?.reference || invoiceId}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("PDF downloaded successfully");
  } catch (error) {
    console.error("Error downloading PDF:", error);
    toast.error("Error downloading PDF");
  } finally {
    setDownloadingPdf(null);
  }
};

  const fetchInvoices = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (searchTerm) params.append("search", searchTerm);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (statusFilter) params.append("status", statusFilter);
      params.append("page", page.toString());

      const response = await axios.get(`${API_URL}/invoices?${params}`, config_token);

      if (response.data.status) {
        const invoicesData = response.data.data.data || response.data.data || [];
        setInvoices(invoicesData);
        setCurrentPage(response.data.data.current_page || 1);
        setLastPage(response.data.data.last_page || 1);
        setTotal(response.data.data.total ?? invoicesData.length);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Appliquer les filtres à chaque changement
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInvoices(1);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, startDate, endDate, statusFilter]);

  const filteredInvoices = useMemo(() => {
    // Le filtrage est déjà fait côté serveur via fetchInvoices
    // Mais on garde un filtre local pour la recherche instantanée
    if (!searchTerm && !startDate && !endDate && !statusFilter) return invoices;

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    return invoices
      .filter(
        (invoice) =>
          invoice.reference.toLowerCase().includes(lowercasedSearchTerm) ||
          invoice.customer_name.toLowerCase().includes(lowercasedSearchTerm) ||
          invoice.customer_email.toLowerCase().includes(lowercasedSearchTerm) ||
          (searchTerm && invoice.total_amount.toString().includes(searchTerm))
      )
      .filter((invoice) => {
        if (statusFilter && invoice.status !== statusFilter) return false;
        if (startDate && new Date(invoice.issue_date) < new Date(startDate)) return false;
        if (endDate && new Date(invoice.issue_date) > new Date(endDate)) return false;
        return true;
      });
  }, [invoices, searchTerm, startDate, endDate, statusFilter]);

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow === null) return;

    try {
      const invoiceToDelete = filteredInvoices[selectedRow];
      await axios.delete(`${API_URL}/invoices/${invoiceToDelete.id}`, config_token);

      setInvoices((prev) => prev.filter((invoice) => invoice.id !== invoiceToDelete.id));
      fetchInvoices(currentPage);
      toast.success("Invoice deleted successfully");
    } catch (error: any) {
      console.error("Error deleting invoice:", error.response || error.message);
      toast.error("Error deleting invoice.");
    } finally {
      setShowDeleteModal(false);
      setSelectedRow(null);
    }
  };

  const handleMarkAsPaid = async (id: number) => {
    try {
      const response = await axios.put(`${API_URL}/invoices/${id}/mark-as-paid`, {}, config_token);
      if (response.data.status) {
        toast.success("Invoice marked as paid");
        fetchInvoices(currentPage);
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Error updating invoice.");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
    fetchInvoices(1);
  };

  const handleEditClick = (id: number) => navigate(`/admin/edit-invoice/${id}`);

  if (loading && invoices.length === 0) {
    return <SkeletonLoader />;
  }

  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: colors.bgPage, padding: "20px", boxSizing: "border-box", color: colors.text }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* EN-TÊTE */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px 0", color: colors.primary, letterSpacing: "-0.02em" }}>
            Manage Invoices
          </h1>
          <p style={{ fontSize: 13.5, color: colors.textMuted, margin: 0 }}>
            <strong style={{ color: colors.primary }}>{total}</strong> invoice{total !== 1 ? "s" : ""} total
            {searchTerm || statusFilter || startDate || endDate ? (
              <span style={{ color: colors.textFaint }}> · {filteredInvoices.length} matching filters</span>
            ) : null}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={() => fetchInvoices(currentPage)}
            style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`, background: "#fff", color: colors.text, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
          >
            Refresh
          </button>
          <button
            onClick={() => navigate("/admin/add-invoice")}
            style={{ padding: "9px 18px", background: colors.primary, color: "#fff", borderRadius: 8, fontSize: 12.5, fontWeight: 600, textDecoration: "none", display: "flex", alignItems: "center", gap: 6, border: "none", cursor: "pointer" }}
          >
            + Add Invoice
          </button>
        </div>
      </div>

      {/* BLOC PRINCIPAL */}
      <div style={{ backgroundColor: "#fff", borderRadius: 12, border: `1px solid ${colors.borderLight}`, boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)", overflow: "hidden" }}>

        {/* BARRE DE FILTRES */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.borderLight}` }}>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg-4">
              <label style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, display: "block", marginBottom: 5 }}>Search</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  placeholder="Reference, customer name, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ ...inputStyle, padding: "9px 32px 9px 12px" }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: colors.textFaint }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="col-6 col-lg-2">
              <label style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, display: "block", marginBottom: 5 }}>Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
            </div>
            <div className="col-6 col-lg-2">
              <label style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, display: "block", marginBottom: 5 }}>End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
            </div>
            <div className="col-6 col-lg-2">
              <label style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, display: "block", marginBottom: 5 }}>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-6 col-lg-2" style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => fetchInvoices(1)}
                style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: "none", background: colors.primary, color: "#fff", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
              >
                Apply
              </button>
              {(searchTerm || statusFilter || startDate || endDate) && (
                <button
                  type="button"
                  onClick={clearSearch}
                  title="Clear filters"
                  style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${colors.border}`, background: "#fff", color: colors.textMuted, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {loading ? (
          <div style={{ padding: "60px", textAlign: "center", color: colors.textFaint }}>
            <i className="fa-solid fa-circle-notch fa-spin me-2" style={{ color: colors.primary }}></i>
            Loading invoices...
          </div>
        ) : (
          <div className="table-responsive" style={{ margin: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ backgroundColor: colors.bgSubtle, borderBottom: `1px solid ${colors.borderLight}` }}>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: colors.primary }}>Reference</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: colors.primary }}>Customer</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: colors.primary }}>Items</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: colors.primary }}>Amount</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: colors.primary }}>Status</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: colors.primary, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((item, index) => {
                    const statusClass = getStatusClass(item.status);
                    const itemsSummary = getItemsSummary(item);
                    return (
                      <tr key={item.id} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                        <td style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: colors.primary, whiteSpace: "nowrap" }}>
                          {item.reference}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: 13 }}>
                          <div style={{ fontWeight: 600, color: colors.text }}>{item.customer_name}</div>
                          <div style={{ fontSize: 11, color: colors.textFaint }}>{item.customer_email}</div>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: 12.5, color: colors.text, maxWidth: 220 }}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{itemsSummary.primary || "—"}</div>
                          {itemsSummary.extra > 0 && (
                            <span style={{ display: "inline-block", marginTop: 3, padding: "2px 8px", borderRadius: 20, background: colors.bgSubtle, fontSize: 10.5, fontWeight: 600, color: colors.textMuted }}>
                              +{itemsSummary.extra} more line{itemsSummary.extra > 1 ? "s" : ""}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: colors.text, whiteSpace: "nowrap" }}>
                          {item.currency} {formatAmount(item.total_amount)}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              backgroundColor: statusClass.bg,
                              color: statusClass.text,
                              border: `1px solid ${statusClass.border}`,
                              textTransform: "capitalize",
                            }}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: 6 }}>
                         <button 
  type="button" 
  onClick={() => handleDownloadPDF(item.id)} 
  disabled={downloadingPdf === item.id} 
  title="Download PDF" 
  style={{ ...actionBtnStyle, color: colors.primary }}
>
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
</button>

                            {item.status === "pending" && (
                              <button type="button" onClick={() => handleMarkAsPaid(item.id)} title="Mark as Paid" style={{ ...actionBtnStyle, color: "#16a34a" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </button>
                            )}

                            <button type="button" onClick={() => handleEditClick(item.id)} title="Edit" style={{ ...actionBtnStyle, color: colors.primary }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>

                            <button type="button" onClick={() => handleDeleteClick(index)} title="Delete" style={{ ...actionBtnStyle, color: colors.danger }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "48px 20px", color: colors.textFaint, fontSize: 13.5 }}>
                      {searchTerm ? "No invoices found matching your search." : "No invoices found."}
                      {searchTerm && (
                        <div style={{ marginTop: 8 }}>
                          <button type="button" onClick={clearSearch} style={{ background: "none", border: "none", color: colors.primary, cursor: "pointer", fontSize: 12.5, textDecoration: "underline" }}>
                            Clear search
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {invoices.length > 0 && lastPage > 1 && (
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${colors.borderLight}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 12.5, color: colors.textMuted }}>
              Page <strong style={{ color: colors.text }}>{currentPage}</strong> of <strong style={{ color: colors.text }}>{lastPage}</strong>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => currentPage > 1 && fetchInvoices(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ padding: "6px 12px", fontSize: 12.5, fontWeight: 600, border: `1px solid ${colors.border}`, borderRadius: 6, background: "#fff", color: currentPage === 1 ? colors.textFaint : colors.primary, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                Previous
              </button>
              <button
                onClick={() => currentPage < lastPage && fetchInvoices(currentPage + 1)}
                disabled={currentPage === lastPage}
                style={{ padding: "6px 12px", fontSize: 12.5, fontWeight: 600, border: `1px solid ${colors.border}`, borderRadius: 6, background: "#fff", color: currentPage === lastPage ? colors.textFaint : colors.primary, cursor: currentPage === lastPage ? "not-allowed" : "pointer" }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL DE SUPPRESSION */}
      {showDeleteModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div style={{ backgroundColor: "#fff", borderRadius: 12, width: 400, maxWidth: "90%", overflow: "hidden", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${colors.borderLight}` }}>
              <h5 style={{ fontSize: 16, fontWeight: 700, color: colors.text, margin: 0 }}>Confirm Deletion</h5>
              <button type="button" onClick={handleCancelDelete} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 18, color: colors.textMuted }}>&times;</button>
            </div>
            <div style={{ padding: 20, fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px", borderTop: `1px solid ${colors.borderLight}`, backgroundColor: colors.bgSubtle }}>
              <button type="button" onClick={handleCancelDelete} style={{ padding: "8px 14px", backgroundColor: "#fff", color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirmDelete} style={{ padding: "8px 14px", backgroundColor: colors.danger, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListInvoices;